import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import '../style/Friends.css';
import { IoCheckmarkSharp, IoCloseSharp, IoBan, IoChatbubbleEllipses, IoArrowUndo, IoPersonAdd, IoSearch, IoArrowBack, IoStar, IoStarOutline } from "react-icons/io5";
import Avatar from '../components/Avatar';
import ProfileView from '../profile/ProfileView';
import FriendsHome, { ActiveStrip, SuggestionCards, useSuggestions, visibleSuggestions } from './FriendsHome';
import { useFriendsActivity } from './useFriendsActivity';
import { getFriendsOverview, sendFriendRequest, confirmRequest, ignoreFriendRequest, unblockUser, cancelRequest, searchUsers, setFavorite } from '../api/FriendsAPI';
import { useSocket } from '../socket/SocketContext';
import { useLanguage } from '../i18n/LanguageContext';
import { presenceLabelKey } from '../utils/presence';

// Friends and search in one page. Left: one search box over the friend
// lists (tabs) - typing filters your friends at once and, from 3 letters,
// searches everyone else too. Right: the selected person's full profile.

const TABS = ['online', 'all', 'pending', 'blocked'];
const EMPTY = { friends: [], incoming: [], outgoing: [], blocked: [] };
const MIN_REMOTE = 3; // the server refuses shorter entries
const PAGE_SIZE = 20; // the server's page size
const DEBOUNCE_MS = 300;
const STATUS_RANK = { online: 0, idle: 1, dnd: 2 };

const me = () => localStorage.getItem('username');
const token = () => localStorage.getItem('accessToken');
const nameOf = (u) => u.fullName || u.username;
const seedOf = (u) => ({ username: u.username, fullName: u.fullName, imageUrl: u.imageUrl });

const NO_RESULTS = { items: [], page: 1, hasMore: false, loading: false, failed: false };

// Search everyone, debounced. Only the newest request's answer is used - an
// older, slower one would otherwise overwrite the results of what's typed now.
function useUserSearch(query) {
  const entry = query.trim();
  const [state, setState] = useState(NO_RESULTS);
  const latest = useRef(0);
  const timer = useRef(null);

  const fetchPage = useCallback(async (q, page) => {
    clearTimeout(timer.current);
    const id = ++latest.current;
    setState((s) => ({ ...s, loading: true, failed: false }));
    const res = await searchUsers(me(), token(), q, page).catch(() => ({ status: 'fail' }));
    if (id !== latest.current) return;
    if (res.status === 'success') {
      setState((s) => ({
        items: page === 1 ? res.data : [...s.items, ...res.data],
        page,
        hasMore: res.data.length === PAGE_SIZE,
        loading: false,
        failed: false
      }));
    } else {
      setState((s) => ({ ...s, loading: false, failed: true }));
    }
  }, []);

  useEffect(() => {
    latest.current++;
    clearTimeout(timer.current);
    if (entry.length < MIN_REMOTE) {
      setState(NO_RESULTS);
      return undefined;
    }
    setState({ ...NO_RESULTS, loading: true });
    timer.current = setTimeout(() => fetchPage(entry, 1), DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [entry, fetchPage]);

  return {
    ...state,
    active: entry.length >= MIN_REMOTE,
    now: () => entry.length >= MIN_REMOTE && fetchPage(entry, 1),
    more: () => fetchPage(entry, state.page + 1)
  };
}

export default function Friends() {
  const { onlineUsers, unreadFriendRequests, clearFriendRequestNotifications, socket, pushToast, statusOf, connected } = useSocket();
  const { t } = useLanguage();
  const history = useHistory();
  const [lists, setLists] = useState(EMPTY);
  // Bumped on every reload of the lists - the open profile reloads with it,
  // so its buttons follow a change made here or by the other person.
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // { username, seed }
  const search = useUserSearch(query);
  const searchInput = useRef(null);
  const voice = useFriendsActivity();
  const [suggestions, hideSuggestion] = useSuggestions();
  const searching = query.trim().length > 0;

  // Everything in one request (friends, incoming, outgoing, blocked) -
  // re-run after any change here, and whenever the other side changes
  // something (request, accept, cancel, remove, block: socket events below).
  const load = useCallback(async () => {
    const res = await getFriendsOverview(me(), token());
    if (res.status === 'success') setLists(res.data);
    setLoading(false);
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    load();
    clearFriendRequestNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (unreadFriendRequests > 0) {
      load();
      clearFriendRequestNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadFriendRequests]);

  useEffect(() => {
    const s = socket && socket.current;
    if (!s) return undefined;
    const reload = () => load();
    s.on('friendRequestAccepted', reload);
    s.on('friends:changed', reload);
    return () => {
      s.off('friendRequestAccepted', reload);
      s.off('friends:changed', reload);
    };
    // `connected`: socket.current is still null when this page is the first
    // thing opened - re-run once the connection exists, or the listeners
    // are never attached.
  }, [socket, load, connected]);

  const act = async (call) => {
    const res = await call().catch(() => ({ status: 'fail' }));
    if (res.status !== 'success') pushToast(res.message || t('profile.actionFailed'));
    await load();
    return res;
  };

  const accept = (name) => act(() => confirmRequest(me(), token(), name));
  const ignore = (name) => act(() => ignoreFriendRequest(me(), token(), name));
  const cancel = (name) => act(() => cancelRequest(me(), token(), name));
  const unblock = (name) => act(() => unblockUser(me(), token(), name));
  const toggleFavorite = (u) => act(() => setFavorite(u.username, !u.favorite));

  const add = async (u) => {
    const res = await act(() => sendFriendRequest(me(), token(), u.username));
    if (res.status === 'success') pushToast(res.accepted ? t('friends.nowFriends', { name: u.username }) : t('friends.requestSent', { name: u.username }));
  };

  // Chat.js reads this back out of location.state - see the comment on its
  // openChatWith effect.
  const openChat = (u) => history.push('/home/', { openChatWith: seedOf(u) });

  const select = (u) => setSelected({ username: u.username, seed: seedOf(u) });
  const isOnline = (u) => onlineUsers.includes(u.username);
  const stop = (fn) => (ev) => { ev.stopPropagation(); fn(); };

  const matches = useCallback((u) => {
    const q = query.trim().toLocaleLowerCase('tr');
    return !q || u.username.toLocaleLowerCase('tr').includes(q) || (u.fullName || '').toLocaleLowerCase('tr').includes(q);
  }, [query]);
  // Starred first, then online, then by name (the server sorted by name).
  const sortedFriends = useMemo(() => lists.friends.filter(matches).slice()
    .sort((a, b) => Number(!!b.favorite) - Number(!!a.favorite) || Number(isOnline(b)) - Number(isOnline(a))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [lists.friends, matches, onlineUsers]);
  const onlineFriends = sortedFriends.filter(isOnline);

  // "Şu an aktif": online friends, the ones in voice first, then by status.
  const active = useMemo(() => lists.friends.filter(isOnline)
    .map((f) => ({ ...f, status: statusOf(f.username) || 'online', voice: voice[f.username] || null }))
    .sort((a, b) => Number(!!b.voice) - Number(!!a.voice)
      || Number(!!b.favorite) - Number(!!a.favorite)
      || (STATUS_RANK[a.status] || 0) - (STATUS_RANK[b.status] || 0)
      || nameOf(a).localeCompare(nameOf(b), 'tr')),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [lists.friends, onlineUsers, voice, statusOf]);
  const openGroup = (v) => history.push('/home/groups', { focusGroup: { groupId: v.groupId, roomId: v.roomId } });
  // Where each search result stands comes from the lists, not from the
  // result itself: those are reloaded after every change, wherever it was
  // made (a row, the profile pane, the other person), so they're never
  // stale. Friends already show above - the rest of everyone is "others".
  const others = useMemo(() => {
    const names = (arr) => new Set(arr.map((u) => u.username));
    const friends = names(lists.friends);
    const blocked = names(lists.blocked);
    const incoming = names(lists.incoming);
    const outgoing = names(lists.outgoing);
    return search.items
      .filter((u) => !friends.has(u.username) && !blocked.has(u.username))
      .map((u) => ({ ...u, incoming: incoming.has(u.username), outgoing: outgoing.has(u.username) }));
  }, [search.items, lists]);

  const counts = {
    online: lists.friends.filter(isOnline).length,
    all: lists.friends.length,
    pending: lists.incoming.length + lists.outgoing.length,
    blocked: lists.blocked.length
  };

  const row = (u, { sub, buttons, key }) => (
    <li
      key={key || u.username}
      className={`glassCard FriendListRowSelectable${selected && selected.username === u.username ? ' ChActive' : ''}`}
      onClick={() => select(u)}
    >
      <Avatar imageUrl={u.imageUrl} alt={u.username} />
      <div>
        {nameOf(u)}
        <aside className="descGray">{sub}</aside>
        {u.description && <aside className="descGray FriendRowBio">{u.description}</aside>}
      </div>
      {buttons}
    </li>
  );

  const friendRow = (u) => row(u, {
    sub: voice[u.username] && isOnline(u)
      ? `${t(presenceLabelKey(statusOf(u.username)))} · ${t('people.inVoice')}`
      : t(presenceLabelKey(statusOf(u.username))),
    buttons: (
      <>
        <button onClick={stop(() => toggleFavorite(u))} className={`FriendRowStar${u.favorite ? ' FriendRowStarOn' : ''}`}
          title={u.favorite ? t('profile.unfavorite') : t('profile.favorite')} aria-label={u.favorite ? t('profile.unfavorite') : t('profile.favorite')}>
          {u.favorite ? <IoStar /> : <IoStarOutline />}
        </button>
        <button onClick={stop(() => openChat(u))} className="AddButton primaryColor" title={t('common.sendMessage')}><IoChatbubbleEllipses /></button>
      </>
    )
  });

  // Starred friends get their own section on top - only once there are any.
  const friendSections = (list) => {
    const starred = list.filter((u) => u.favorite);
    if (starred.length === 0) return <ul className="FriendList">{list.map(friendRow)}</ul>;
    const rest = list.filter((u) => !u.favorite);
    return (
      <>
        {sectionTitle(t('people.favoritesSection', { count: starred.length }))}
        <ul className="FriendList">{starred.map(friendRow)}</ul>
        {rest.length > 0 && sectionTitle(t('people.restSection', { count: rest.length }))}
        <ul className="FriendList">{rest.map(friendRow)}</ul>
      </>
    );
  };

  const incomingButtons = (onAccept, onIgnore) => (
    <>
      <button onClick={stop(onAccept)} className="AddButton primaryColor" title={t('friends.accept')}><IoCheckmarkSharp /></button>
      <button onClick={stop(onIgnore)} className="AddButton cancelColor" title={t('friends.ignore')}><IoCloseSharp /></button>
    </>
  );
  const cancelButton = (onCancel) => (
    <button onClick={stop(onCancel)} className="AddButton secondaryColor" title={t('profile.cancelRequest')}><IoArrowUndo /></button>
  );

  const resultRow = (u) => row(u, {
    sub: `@${u.username} · ${t(presenceLabelKey(statusOf(u.username)))}`,
    buttons: u.incoming ? incomingButtons(() => accept(u.username), () => ignore(u.username))
      : u.outgoing ? cancelButton(() => cancel(u.username))
        : <button onClick={stop(() => add(u))} className="AddButton primaryColor" title={t('profile.addFriend')}><IoPersonAdd /></button>
  });

  const emptyText = (key) => <p className="descGray FriendListEmpty">{t(key)}</p>;
  const sectionTitle = (text) => <h4 className="FriendListSectionTitle">{text}</h4>;

  const searchResults = (
    <>
      {sortedFriends.length > 0 && (
        <>
          {sectionTitle(t('people.friendsSection', { count: sortedFriends.length }))}
          <ul className="FriendList">{sortedFriends.map(friendRow)}</ul>
        </>
      )}
      {!search.active && emptyText('people.typeMore')}
      {search.active && (
        <>
          {sectionTitle(t('people.othersSection'))}
          <ul className="FriendList">{others.map(resultRow)}</ul>
          {search.loading && <p className="descGray FriendListEmpty">{t('people.searching')}</p>}
          {!search.loading && search.failed && emptyText('people.searchFailed')}
          {!search.loading && !search.failed && others.length === 0 && emptyText('people.noResults')}
          {!search.loading && search.hasMore && (
            <button type="button" className="SendButton secondaryColor PeopleMore" onClick={search.more}>{t('people.more')}</button>
          )}
        </>
      )}
    </>
  );

  const tabContent = (
    <>
      {tab === 'online' && (
        <>
          {!loading && onlineFriends.length === 0 && emptyText('friends.emptyOnline')}
          {friendSections(onlineFriends)}
        </>
      )}

      {tab === 'all' && (
        <>
          {!loading && lists.friends.length === 0 && emptyText('friends.empty')}
          {friendSections(sortedFriends)}
          {/* Phones only - on desktop these are in the right pane. */}
          {visibleSuggestions(suggestions, lists).length > 0 && (
            <div className="PeopleMobileOnly">
              {sectionTitle(t('people.suggestionsTitle'))}
              <SuggestionCards items={visibleSuggestions(suggestions, lists)} lists={lists} className="PeopleSuggestStrip"
                onSelect={select} onAdd={add} onCancel={(u) => cancel(u.username)} onMessage={openChat} onHide={hideSuggestion} />
            </div>
          )}
        </>
      )}

      {tab === 'pending' && (
        <>
          {!loading && counts.pending === 0 && emptyText('friends.emptyPending')}
          {lists.incoming.length > 0 && (
            <>
              {sectionTitle(t('friends.incoming', { count: lists.incoming.length }))}
              <ul className="FriendList">
                {lists.incoming.map((u) => row(u, { sub: `@${u.username}`, buttons: incomingButtons(() => accept(u.username), () => ignore(u.username)) }))}
              </ul>
            </>
          )}
          {lists.outgoing.length > 0 && (
            <>
              {sectionTitle(t('friends.outgoing', { count: lists.outgoing.length }))}
              <ul className="FriendList">
                {lists.outgoing.map((u) => row(u, { sub: `@${u.username}`, buttons: cancelButton(() => cancel(u.username)) }))}
              </ul>
            </>
          )}
        </>
      )}

      {tab === 'blocked' && (
        <ul className="FriendList">
          {!loading && lists.blocked.length === 0 && emptyText('friends.emptyBlocked')}
          {lists.blocked.map((u) => row(u, {
            sub: `@${u.username}`,
            buttons: <button onClick={stop(() => unblock(u.username))} className="AddButton primaryColor" title={t('friends.unblock')}><IoBan /></button>
          }))}
        </ul>
      )}
    </>
  );

  return (
    <div className={selected ? "SplitPage HasSelection" : "SplitPage"}>
      <div className="ChannelContainer glass">
        <h4>{t('friends.title')}</h4>
        <p className="descGray">{t('people.subtitle')}</p>

        <div className="PeopleSearch">
          <IoSearch />
          <input
            id="PeopleSearchInput"
            ref={searchInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); search.now(); }
              if (e.key === 'Escape') setQuery('');
            }}
            placeholder={t('people.searchPlaceholder')}
            aria-label={t('people.searchPlaceholder')}
            autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
          />
          {searching && (
            <button type="button" className="PeopleSearchClear" onClick={() => setQuery('')} title={t('people.clearSearch')} aria-label={t('people.clearSearch')}><IoCloseSharp /></button>
          )}
        </div>

        {!searching && <ActiveStrip active={active} onSelect={select} />}

        {!searching && (
          <div className="FriendTabs" role="tablist">
            {TABS.map((key) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key}
                className={`FriendTab${tab === key ? ' FriendTabActive' : ''}`} onClick={() => setTab(key)}>
                {t(`friends.tabs.${key}`)}
                {counts[key] > 0 && <span className={`FriendTabCount${key === 'pending' && lists.incoming.length > 0 ? ' FriendTabCountAlert' : ''}`}>{counts[key]}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="FriendListScroll">
          {searching ? searchResults : tabContent}
        </div>
      </div>

      <div className="ChatContainer">
        {selected ? (
          <div className="PeoplePane glass">
            <button type="button" className="PeopleBack" onClick={() => setSelected(null)} title={t('common.back')} aria-label={t('common.back')}><IoArrowBack /></button>
            <ProfileView
              key={selected.username}
              username={selected.username}
              seed={selected.seed}
              onSelectUser={select}
              onChanged={load}
              version={version}
            />
          </div>
        ) : (
          <FriendsHome
            lists={lists}
            loaded={!loading}
            active={active}
            suggestions={suggestions}
            onHide={hideSuggestion}
            onOpenGroup={openGroup}
            onSelect={select}
            onAccept={(u) => accept(u.username)}
            onIgnore={(u) => ignore(u.username)}
            onAdd={add}
            onCancel={(u) => cancel(u.username)}
            onMessage={openChat}
            onFindPeople={() => searchInput.current && searchInput.current.focus()}
          />
        )}
      </div>
    </div>
  );
}
